import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Wallet, CheckCircle, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import api from '@/services/api';
import toast from 'react-hot-toast';

interface Rule {
  id: string;
  version: number;
  box_size: number;
  num_new_donors: number;
  num_bridges: number;
  donation_amount: number;
  pct_receiver: number;
  pct_fund: number;
  pct_fee: number;
  max_boxes_per_user: number;
  box_timeout_hours: number;
  active_from: string;
  active_to: string | null;
}

interface PaymentIntent {
  id: string;
  status: string;
  client_secret: string;
  provider: string;
  sandbox?: boolean;
}

type Step = 'choose' | 'payment' | 'processing';

export default function JoinMovementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('choose');
  const [paymentProvider, setPaymentProvider] = useState<string>('stripe');
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);

  /* Tutte le regole */
  const { data: allRulesData, isLoading: rulesLoading } = useQuery<{ rules: Rule[] }>({
    queryKey: ['rules-all'],
    queryFn: async () => {
      const { data } = await api.get('/rules/');
      return data;
    },
  });

  /* Provider pagamento disponibili */
  const { data: providersData } = useQuery<{ providers: Record<string, { name: string; available: boolean }> }>({
    queryKey: ['payment-providers'],
    queryFn: async () => {
      const { data } = await api.get('/payments/providers');
      return data;
    },
  });

  const allRules: Rule[] = allRulesData?.rules ?? [];
  const activeRule = allRules.find((r) => !r.active_to);
  const chosenRule = selectedRuleId
    ? allRules.find((r) => r.id === selectedRuleId)
    : activeRule;

  const { data: statusData } = useQuery({
    queryKey: ['movement-status'],
    queryFn: async () => {
      const { data } = await api.get('/movement/status');
      return data;
    },
  });

  // Step 1: Crea payment intent
  const createIntentMutation = useMutation({
    mutationFn: async () => {
      const amountCents = (chosenRule?.donation_amount ?? 100) * 100;
      const { data } = await api.post('/payments/intent', {
        box_id: 'pending', // sarà assegnato dal backend
        amount: amountCents,
        payment_provider: paymentProvider,
      });
      return data;
    },
    onSuccess: (data) => {
      setPaymentIntent(data.intent);
      setStep('payment');
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Errore nella creazione del pagamento';
      toast.error(message);
    },
  });

  // Step 2: Conferma pagamento e join
  const confirmPaymentMutation = useMutation({
    mutationFn: async () => {
      setStep('processing');
      // Verifica pagamento
      const verifyRes = await api.post('/payments/verify', {
        payment_id: paymentIntent!.id,
        payment_provider: paymentProvider,
      });

      if (verifyRes.data.payment?.status !== 'succeeded') {
        throw new Error('Il pagamento non è stato confermato');
      }

      // Procedi con il join
      const payload: Record<string, unknown> = {
        payment_id: paymentIntent!.id,
        payment_provider: paymentProvider,
      };
      if (selectedRuleId) payload.rule_id = selectedRuleId;
      const { data } = await api.post('/movement/join', payload);
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Pagamento confermato! Sei entrato nel Movimento del Dono!');
      queryClient.invalidateQueries({ queryKey: ['movement-status'] });
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      queryClient.invalidateQueries({ queryKey: ['ledger-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      navigate(`/boxes`, { replace: true });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Errore durante il pagamento o l\'ingresso';
      toast.error(message);
      setStep('payment');
    },
  });

  const donation = chosenRule?.donation_amount ?? 100;
  const payout = chosenRule
    ? (chosenRule.donation_amount * chosenRule.pct_receiver / 100 * chosenRule.num_new_donors).toFixed(0)
    : '800';

  const alreadyInMovement = !!statusData?.movement_state;

  const availableProviders = Object.entries(providersData?.providers ?? {}).filter(
    ([, p]) => p.available
  );

  const providerIcons: Record<string, { icon: typeof CreditCard; label: string; desc: string }> = {
    stripe: { icon: CreditCard, label: 'Carta di Credito', desc: 'Paga con Stripe (Visa, Mastercard, ecc.)' },
    blockchain: { icon: Wallet, label: 'Wallet Blockchain', desc: 'Paga con il tuo wallet crypto' },
  };

  return (
    <>
      <Helmet>
        <title>Entra nel Movimento — React-City</title>
      </Helmet>

      <div className="space-y-6 animate-in max-w-2xl mx-auto">
        <div className="text-center">
          <span className="text-5xl block mb-4">�️</span>
          <h1 className="text-3xl font-bold text-heading">
            Entra nel Movimento del Dono
          </h1>
          <p className="text-muted mt-2">
            Dona, accompagna, ricevi. Un ciclo virtuoso di mutuo sostegno.
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-2 px-4">
          {(['choose', 'payment', 'processing'] as Step[]).map((s, i) => {
            const labels = ['Scegli modello', 'Pagamento', 'Conferma'];
            const active = step === s;
            const done = (['choose', 'payment', 'processing'].indexOf(step) > i);
            return (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full transition-all ${done || active ? 'bg-primary-500' : 'bg-surface-200 dark:bg-surface-700'}`} />
                <p className={`text-xs mt-1 text-center ${active ? 'text-primary-500 font-semibold' : 'text-muted'}`}>{labels[i]}</p>
              </div>
            );
          })}
        </div>

        {/* ─── STEP 1: Scelta modello ─── */}
        {step === 'choose' && (
          <>
            {/* Scegli il modello — PRIMA DI TUTTO */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-heading">📋 Scegli il modello</h2>
              <p className="text-muted text-sm">Seleziona la regola da applicare al tuo ingresso nel City-Box.</p>

              {rulesLoading ? (
                <div className="card text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto mb-2" />
                  <p className="text-muted text-sm">Caricamento modelli...</p>
                </div>
              ) : allRules.length === 0 ? (
                <div className="card text-center py-8">
                  <p className="text-muted text-sm">Nessun modello disponibile al momento.</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {allRules.map((r) => {
                    const isActive = !r.active_to;
                    const isSelected = selectedRuleId ? r.id === selectedRuleId : isActive;
                    const expectedPayout = (r.donation_amount * r.pct_receiver / 100 * r.num_new_donors).toFixed(0);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedRuleId(r.id)}
                        className={`card text-left transition-all cursor-pointer ${
                          isSelected
                            ? 'ring-2 ring-primary-500 border-primary-500 dark:border-primary-400'
                            : 'hover:border-surface-300 dark:hover:border-surface-600'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-heading">
                            v{r.version} — {r.box_size} posti
                            {isActive && <span className="badge-green ml-2 text-xs">Attiva</span>}
                          </span>
                          {isSelected && <CheckCircle className="w-5 h-5 text-primary-500" />}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                          <div><p className="text-muted text-xs">Donazione</p><p className="font-bold text-primary-500">€{r.donation_amount}</p></div>
                          <div><p className="text-muted text-xs">Payout potenziale</p><p className="font-bold text-accent-500">€{expectedPayout}</p></div>
                          <div><p className="text-muted text-xs">Distribuzione</p><p className="font-semibold text-heading">{r.pct_receiver}/{r.pct_fund}/{r.pct_fee}%</p></div>
                          <div><p className="text-muted text-xs">Composizione</p><p className="font-semibold text-heading">{r.num_new_donors}D + {r.num_bridges}B + 1R</p></div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info donazione */}
            <div className="card-glow">
              <h2 className="text-lg font-semibold text-heading mb-4">Il tuo contributo</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary-500 dark:text-primary-400">€{donation}</p>
                  <p className="text-muted text-sm mt-1">Donazione di ingresso</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-accent-500 dark:text-accent-400">€{payout}</p>
                  <p className="text-muted text-sm mt-1">Payout potenziale</p>
                </div>
              </div>
            </div>

            {/* Come funziona */}
            <div className="card">
              <h2 className="text-lg font-semibold text-heading mb-4">Come funziona</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="slot-donor flex-shrink-0 text-sm">1</div>
                  <div>
                    <p className="font-medium text-heading">Nuovo Donatore</p>
                    <p className="text-muted text-sm">Entri in un City-Box e doni €{donation}. Verrai assegnato automaticamente al box migliore.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="slot-bridge flex-shrink-0 text-sm">2</div>
                  <div>
                    <p className="font-medium text-heading">Bridge</p>
                    <p className="text-muted text-sm">Quando il box si completa, avanzi come Bridge nel box successivo. Guidi i nuovi donatori.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="slot-receiver flex-shrink-0 text-sm">3</div>
                  <div>
                    <p className="font-medium text-heading">Receiver</p>
                    <p className="text-muted text-sm">Diventi Receiver nel tuo box e ricevi il payout (€{payout}). Puoi reinvestire o uscire.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stato corrente */}
            {alreadyInMovement && (
              <div className="card border-accent-200 dark:border-accent-500/30">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-heading">Sei già nel Movimento</p>
                    <p className="text-muted text-sm">
                      Stato attuale: <span className="badge-green">{statusData.movement_state.state}</span> nel box #{statusData.movement_state.box_id?.slice(0, 6)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Conferma */}
            <div className="card">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-surface-300 text-primary-600
                             focus:ring-primary-500 dark:bg-surface-800 dark:border-surface-600"
                />
                <span className="text-sm text-muted">
                  Comprendo che la mia donazione di €{donation} è volontaria e irrevocabile.
                  Accetto le regole del Movimento del Dono e la politica di trasparenza.
                </span>
              </label>

              <button
                onClick={() => setStep('payment')}
                disabled={!confirmed}
                className="btn-primary w-full mt-6"
              >
                <ArrowRight className="w-4 h-4" />
                Procedi al Pagamento — €{donation}
              </button>
            </div>
          </>
        )}

        {/* ─── STEP 2: Pagamento ─── */}
        {step === 'payment' && (
          <>
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-heading">Scegli metodo di pagamento</h2>
                <button onClick={() => setStep('choose')} className="btn-ghost text-sm">
                  <ArrowLeft className="w-4 h-4" /> Indietro
                </button>
              </div>

              {/* Riepilogo ordine con modello scelto */}
              <div className="bg-surface-100 dark:bg-surface-800 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-muted">Donazione di ingresso</p>
                    <p className="text-heading font-semibold">City-Box {chosenRule ? `v${chosenRule.version} — ${chosenRule.box_size} posti` : ''}</p>
                  </div>
                  <p className="text-2xl font-bold text-primary-500">€{donation}</p>
                </div>
                {chosenRule && (
                  <div className="grid grid-cols-3 gap-2 text-xs text-muted mt-2 pt-2 border-t border-surface-200 dark:border-surface-700">
                    <div>Payout: <span className="text-accent-500 font-semibold">€{payout}</span></div>
                    <div>Distribuzione: <span className="text-heading font-semibold">{chosenRule.pct_receiver}/{chosenRule.pct_fund}/{chosenRule.pct_fee}%</span></div>
                    <div>Composizione: <span className="text-heading font-semibold">{chosenRule.num_new_donors}D+{chosenRule.num_bridges}B+1R</span></div>
                  </div>
                )}
              </div>

              {/* Provider selection */}
              <div className="space-y-3 mb-6">
                {(availableProviders.length > 0 ? availableProviders : [['stripe', { name: 'stripe', available: true }] as const]).map(
                  ([key]) => {
                    const info = providerIcons[key] || { icon: CreditCard, label: key, desc: '' };
                    const Icon = info.icon;
                    const isSelected = paymentProvider === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setPaymentProvider(key)}
                        className={`w-full card text-left transition-all cursor-pointer flex items-center gap-4 ${
                          isSelected
                            ? 'ring-2 ring-primary-500 border-primary-500 dark:border-primary-400'
                            : 'hover:border-surface-300 dark:hover:border-surface-600'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-primary-500/10 text-primary-500' : 'bg-surface-100 dark:bg-surface-800 text-muted'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-heading">{info.label}</p>
                          <p className="text-xs text-muted">{info.desc}</p>
                        </div>
                        {isSelected && <CheckCircle className="w-5 h-5 text-primary-500" />}
                      </button>
                    );
                  }
                )}
              </div>

              {/* Stripe sandbox notice */}
              <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-700/30 rounded-xl p-3 mb-6">
                <p className="text-xs text-yellow-700 dark:text-yellow-400">
                  🔒 <strong>Ambiente sicuro</strong> — I pagamenti sono gestiti in modalità sandbox. Nessun addebito reale verrà effettuato.
                </p>
              </div>

              {/* Pay button */}
              <button
                onClick={() => {
                  if (paymentIntent) {
                    confirmPaymentMutation.mutate();
                  } else {
                    createIntentMutation.mutate();
                  }
                }}
                disabled={createIntentMutation.isPending || confirmPaymentMutation.isPending}
                className="btn-primary w-full"
              >
                {createIntentMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Creazione pagamento...
                  </span>
                ) : paymentIntent ? (
                  <span className="flex items-center justify-center gap-2">
                    {confirmPaymentMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Conferma in corso...</>
                    ) : (
                      <><CheckCircle className="w-4 h-4" /> Conferma Pagamento — €{donation}</>
                    )}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard className="w-4 h-4" /> Paga €{donation}
                  </span>
                )}
              </button>
            </div>
          </>
        )}

        {/* ─── STEP 3: Processing ─── */}
        {step === 'processing' && (
          <div className="card text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-heading">Elaborazione in corso...</h2>
            <p className="text-muted text-sm mt-2">Stiamo verificando il pagamento e attivando il tuo City-Box.</p>
          </div>
        )}
      </div>
    </>
  );
}
